import pack from '../../package.json'
import { format } from "date-fns";

// src/helpers/sessionTracker.ts
let inactivityTimeout: NodeJS.Timeout | null = null;
// let lastActivityTime = Date.now();
let screenTimeInterval: NodeJS.Timeout | null = null;
let isTabVisible = true;
let boundHandleVisibilityChange: (() => void) | null = null;

export type SessionEvent = {
  event_date: string;
  visit_id: string;
  user_id: number | null;
  user_name: string;
  event_type: "visit" | "find" | "download" | "link";
  search_parameters: string;
  time_spent: number;
  isPaused?: boolean;
}

const callbackOnExit = () => {
  const interacted = localStorage.getItem("userInteracted") === "true";
  if (!interacted) {
    console.log("here I am!!")
    const sessionDataStr = localStorage.getItem("sessionData");
    if (!sessionDataStr) return;

    const sessionData = JSON.parse(sessionDataStr);

    if (sessionData && sessionData.visit_id) {
      const params = new URLSearchParams({
        ...sessionData,
        ENVTYPE: `${process.env.REACT_APP_ENV}-${process.env.REACT_APP_CLIENT_DB}`,
        ClientDb: process.env.REACT_APP_CLIENT_DB,
        APP_VERSION: pack.version,
      }).toString();
      const url = `${process.env.REACT_APP_BASE_URL}/api/v1/event_analytics?${params}`;
      navigator.sendBeacon(url, "");
    }
  }
}

// 🔹 Start a new session or reuse existing visit_id
export const startSession = async (userData: { id: number; email: string } | null) => {
  let visitId = localStorage.getItem("visitId");

  if (!visitId) {
    visitId = `visit_${Date.now()}`;
    localStorage.setItem("visitId", visitId);
  }

  const sessionData: SessionEvent = {
    event_date: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    visit_id: visitId,
    user_id: userData ? userData.id : null,
    user_name: userData ? userData.email : "Not Logged In",
    event_type: "visit",
    search_parameters: "",
    time_spent: 0,
    isPaused: false,
  };

  localStorage.setItem("sessionData", JSON.stringify(sessionData));
  startScreenTimeTracking();
  localStorage.setItem("wasSessionEnded", "false");
};

// sessionTracker.ts

let sendEventApi: ((body: any) => void) | null = null;

export const registerEventApi = (mutateFn: (body: any) => void) => {
  sendEventApi = mutateFn;
};

// inside trackEvent
export const trackEvent = (eventType: string, searchParam?: string) => {
  const visitId = localStorage.getItem("visitId");
  if (!visitId) return;

  const sessionDataStr = localStorage.getItem("sessionData");
  if (!sessionDataStr) return;

  const sessionData = JSON.parse(sessionDataStr);

  // const eventPayload = {
  //   event_date: new Date().toISOString(),
  //   visit_id: visitId,
  //   user_id: sessionData.user_id,
  //   user_name: sessionData.user_name,
  //   event_type: eventType,
  //   search_parameters: searchParam || null,
  //   time_spent: sessionData.time_spent,
  // };

  // ✅ mark that user interacted (other than visit)
  if (eventType !== "visit") {
    localStorage.setItem("userInteracted", "true");
  }

  // ✅ Update sessionData in real-time
  sessionData.event_type = eventType;
  sessionData.search_parameters = searchParam || null;
  sessionData.event_date = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'");
  console.log({ sessionData })

  if (sendEventApi) {
    sendEventApi(sessionData);
  }

  // ✅ Reset time_spent after sending
  sessionData.time_spent = 0;

  localStorage.setItem("sessionData", JSON.stringify(sessionData));
};



// 🔹 Pause session (tab hidden, user inactive)
export const pauseSession = () => {
    const sessionData: SessionEvent = JSON.parse(
        localStorage.getItem("sessionData") || "{}"
    );
    if (!sessionData.isPaused) {
        sessionData.isPaused = true;
        localStorage.setItem("sessionData", JSON.stringify(sessionData));

        if (screenTimeInterval) {
            clearInterval(screenTimeInterval);
            screenTimeInterval = null;
        }
    }
};

// 🔹 Resume session (tab visible again)
export const resumeSession = () => {
    const sessionData: SessionEvent = JSON.parse(
        localStorage.getItem("sessionData") || "{}"
    );
    if (sessionData.isPaused) {
        sessionData.isPaused = false;
        localStorage.setItem("sessionData", JSON.stringify(sessionData));

        if (!screenTimeInterval) {
            startScreenTimeTracking();
        }
    }
};

// 🔹 End session (after 5 min inactivity)
export const endSession = async () => {
  const sessionData: SessionEvent = JSON.parse(
    localStorage.getItem("sessionData") || "{}"
  );
  if (!sessionData.visit_id) return;

  if (sessionData.event_type === 'visit') {
    sendEvent(sessionData);
  }

  document.removeEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      callbackOnExit()
    }
  })
  window.addEventListener("pagehide", callbackOnExit);
  window.addEventListener("beforeunload", callbackOnExit);

  document.removeEventListener("mousemove", () => {})
  document.removeEventListener("keydown", () => {})
  if (screenTimeInterval) {
    clearInterval(screenTimeInterval);
    screenTimeInterval = null;
  }

  localStorage.setItem("wasSessionEnded", "true");
  localStorage.removeItem("screen_time");
  localStorage.removeItem("sessionData"); // ✅ keep visitId intact
  localStorage.removeItem("userInteracted")
};

// 🔹 Handle tab visibility changes
export const handleVisibilityChange = async (
    userData: { id: number; email: string } | null
) => {
    if (document.hidden) {
        isTabVisible = false;
        pauseSession();
        resetInactivityTimer();
    } else {
        isTabVisible = true;
        const sessionData = localStorage.getItem("sessionData");
        if (!sessionData) {
            await startSession(userData);
        }
        if (inactivityTimeout) clearTimeout(inactivityTimeout);

        resumeSession();
    }
};

// 🔹 Initialize session tracking
// 🔹 Initialize session tracking
export const initializeSession = async (userData: { id: number; email: string } | null) => {
  const wasSessionEnded = localStorage.getItem("wasSessionEnded") === "true";

  if (wasSessionEnded) {
    await startSession(userData);
  } else {
    const existingSession = localStorage.getItem("sessionData");
    if (!existingSession) {
      await startSession(userData);
    }
  }

  if (!boundHandleVisibilityChange) {
    boundHandleVisibilityChange = () => handleVisibilityChange(userData);
    document.addEventListener("visibilitychange", boundHandleVisibilityChange);
  }

  document.addEventListener("mousemove", resetInactivityTimer);
  document.addEventListener("keydown", resetInactivityTimer);

  // 🔹 NEW: Handle tab close (only if no user interaction)
  window.addEventListener("pagehide", callbackOnExit);
  window.addEventListener("beforeunload", callbackOnExit);
};

// 🔹 Reset inactivity timer
const resetInactivityTimer = () => {
    // lastActivityTime = Date.now();
    if (inactivityTimeout) clearTimeout(inactivityTimeout);

    inactivityTimeout = setTimeout(async () => {
        if (!isTabVisible) {
            // ✅ only send if user never interacted
            const interacted = localStorage.getItem("userInteracted") === "true";
            if (!interacted) {
                localStorage.setItem("wasSessionEnded", "true");
                endSession();
            }
        }
    }, 1000*60*5); // 5 minutes
};

// 🔹 Screen time tracking
const startScreenTimeTracking = () => {
  if (screenTimeInterval) {
    clearInterval(screenTimeInterval);
    screenTimeInterval = null;
  }

  screenTimeInterval = setInterval(() => {
    const sessionData: SessionEvent = JSON.parse(
      localStorage.getItem("sessionData") || "{}"
    );
    if (sessionData && !sessionData.isPaused) {
      sessionData.time_spent += 1;
      localStorage.setItem("screen_time", String(sessionData.time_spent));
      localStorage.setItem("sessionData", JSON.stringify(sessionData));
    }
  }, 1000);
};

// 🔹 Send event to backend
const sendEvent = (sessionData: SessionEvent) => {
  const { isPaused, ...trimmed_session } = sessionData
  const params = new URLSearchParams({
    ...trimmed_session,
    user_id: sessionData.user_id ? sessionData.user_id.toString() : "",
    time_spent: sessionData.time_spent.toString(),
    ENVTYPE: `${process.env.REACT_APP_ENV}-${process.env.REACT_APP_CLIENT_DB}`,
    ClientDb: process.env.REACT_APP_CLIENT_DB || "",
    APP_VERSION: pack.version,
  });

  navigator.sendBeacon(`${process.env.REACT_APP_BASE_URL}/api/v1/event_analytics?${params}`, "");

  // ✅ Reset time after successful send
  // sessionData.time_spent = 0;
  // localStorage.setItem("sessionData", JSON.stringify(sessionData));
};
